import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AccountCircle from "@mui/icons-material/AccountCircle";
import SidebarMenu from "./sideBarMenu";
import logo from "../assets/product_logo_with_name.png";
import logo2 from "../assets/product_logo.png";
import Grid from "@mui/material/Grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import LinkIcon from "@mui/icons-material/Link";
import CategoryIcon from "@mui/icons-material/Category";
import LayersIcon from "@mui/icons-material/Layers";
import { logoutApi } from "../Api/logoutApi";

const drawerWidth = 265;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: "#3599E2",
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("md")]: {
      marginLeft: 0,
      width: "100%",
    },
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      backgroundColor: "#3599E2",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      backgroundColor: "#3599E2",
    },
  }),
}));

const settings = ["Logout"];

const ListItemLink = ({
  to,
  icon,
  primary,
  open,
  setOpen,
  navigate,
  isMobile,
}) => (
  <ListItem
    disablePadding
    sx={{ display: "block" }}
    onClick={() => {
      navigate(to);
      // Close drawer on mobile after navigation
      if (isMobile) {
        setOpen(false);
      }
    }}
  >
    <ListItemButton
      sx={{
        minHeight: 48,
        justifyContent: open ? "initial" : "center",
        px: 2.5,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: open ? 3 : "auto",
          justifyContent: "center",
          color: "white",
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={primary}
        sx={{
          opacity: open ? 1 : 0,
          color: "white",
          "& .MuiTypography-root": {
            fontSize: "14px",
          },
        }}
      />
    </ListItemButton>
  </ListItem>
);

const UserMenu = ({
  anchorElUser,
  handleOpenUserMenu,
  handleCloseUserMenu,
  navigate,
  isMobile,
}) => (
  <Box sx={{ flexGrow: 0 }}>
    <Tooltip title="Open settings">
      <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
        <AccountCircle
          sx={{
            width: isMobile ? 40 : 50,
            height: isMobile ? 40 : 50,
            color: "white",
          }}
        />
      </IconButton>
    </Tooltip>
    <Menu
      sx={{ mt: "45px" }}
      id="menu-appbar"
      anchorEl={anchorElUser}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={Boolean(anchorElUser)}
      onClose={handleCloseUserMenu}
    >
      {settings.map((setting) => (
        <MenuItem
          key={setting}
          onClick={async () => {
            handleCloseUserMenu();

            if (setting === "Profile") {
              navigate("/profile");
            }

            if (setting === "Logout") {
              try {
                await logoutApi();
                sessionStorage.clear();
                navigate("/login");
              } catch (err) {
                console.error("Logout failed", err);
                sessionStorage.clear();
                navigate("/login");
              }
            }
          }}
        >
          <Typography textAlign="center">{setting}</Typography>
        </MenuItem>
      ))}
    </Menu>
  </Box>
);

const routes = [
  {
    path: "/",
    name: "Dashboard",
    icon: <HomeIcon />,
  },
  
  {
    path: "/UncategorizedGrouped",
    name: "Uncategorized URL",
    icon: <LayersIcon />, // Represents grouping/layers
  },
  {
    path: "/Url",
    name: "Classify URL",
    icon: <LinkIcon />, // Perfect for URLs
  },
  // {
  //   path: "/SetCategory",
  //   name: "Set Category",
  //   icon: <CategoryIcon />, // Best for categories
  // },
];

export default function SideBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(!isMobile);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  // Handle mobile drawer
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Update drawer state when screen size changes
  React.useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isMobile]);

  const drawer = (
    <div>
      <DrawerHeader>
        <IconButton
          color="inherit"
          aria-label="brand logo"
          edge="start"
          sx={{ mr: 2 }}
        >
          <img
            src={logo2}
            alt="Logo"
            style={{
              height: 40,
              marginRight: 16,
            }}
          />
        </IconButton>
        {!isMobile && (
          <IconButton onClick={handleDrawerClose} sx={{ color: "white" }}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        )}
      </DrawerHeader>
      <Divider sx={{ bgcolor: "rgba(255, 255, 255, 0.2)" }} />
      <List>
        {routes.map((route, index) =>
          route.subRoutes ? (
            <SidebarMenu
              key={index}
              route={route}
              isOpen={open || mobileOpen}
              isMobile={isMobile}
              onNavigate={() => isMobile && setMobileOpen(false)}
            />
          ) : (
            <ListItemLink
              key={index}
              to={route.path}
              icon={route.icon}
              primary={route.name}
              open={open || mobileOpen}
              setOpen={isMobile ? setMobileOpen : setOpen}
              navigate={navigate}
              isMobile={isMobile}
            />
          )
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open && !isMobile}>
        <Toolbar>
          <Grid container alignItems="center">
            <Grid item xs={2}>
              {isMobile ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleMobileDrawerToggle}
                  edge="start"
                >
                  <MenuIcon />
                </IconButton>
              ) : !open ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                >
                  <img
                    src={logo}
                    alt="Logo"
                    style={{ height: 40, marginRight: 20 }}
                  />
                </IconButton>
              ) : null}
            </Grid>
            <Grid item xs={8} container justifyContent="center">
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  color: "white",
                  textAlign: "center",
                  fontSize: {
                    xs: "20px",
                    sm: "24px",
                    md: "28px",
                  },
                }}
              >
                <b>Kenrui URL Categorization</b>
              </Typography>
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end">
              <UserMenu
                anchorElUser={anchorElUser}
                handleOpenUserMenu={handleOpenUserMenu}
                handleCloseUserMenu={handleCloseUserMenu}
                navigate={navigate}
                isMobile={isMobile}
              />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleMobileDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#3599E2",
            },
          }}
        >
          {drawer}
        </MuiDrawer>
      ) : (
        /* Desktop Drawer */
        <Drawer variant="permanent" open={open}>
          {drawer}
        </Drawer>
      )}
    </Box>
  );
}
